import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Grid, TextField, Typography } from "@mui/material";
import { Controller } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import FormDialog from "common/Modal/Modal";
import CommonCheckbox from "common/CheckBox/Checkbox";
interface SalesCreditNoteTableProps {
  form?: any;
  columns: any[];
  handleInputChange: (index: number, columnId: string, val: any) => void;
  rows: any[];
  deletedItems: any[];
  maxItemCount: number;
  DeleteItem: (row: any, index: number) => void;
  handleItem?: (row: any) => void;
  restoreDeletedItem: (itemId: number) => void;
  themeColors: any;
}

export const SalesCreditNoteTable: React.FC<SalesCreditNoteTableProps> = ({
  form,
  columns,
  handleInputChange,
  rows,
  DeleteItem,
  deletedItems,
  restoreDeletedItem,
  themeColors,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [confirm, setConfirm] = React.useState<boolean>(false);
  const [confirmRowIndex, setConfirmRowIndex] = React.useState<number | null>(
    null,
  );
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, rows],
  );

  return (
    <Box sx={{ width: "100%" }} id="inventorytable">
      <TableContainer>
        <Table
          sx={{
            minWidth: 750,
            "& tbody tr:nth-child(odd) th, & tbody tr:nth-child(odd) td:last-child":
              { borderRadius: "0 !important" },
          }}
          aria-labelledby="tableTitle"
          size={"small"}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={"left"}
                  padding={column.disablePadding ? "none" : "normal"}
                  sortDirection={false}
                >
                  <span className="font-bold">{column.label}</span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => {
              const labelId = `enhanced-table-checkbox-${index}`;
              const selectedItemIds = rows
                .filter((_, i) => i !== index)
                .map((r) => r.itemId)
                .filter(Boolean);
              const itemOptions = deletedItems
                .filter(
                  (item) =>
                    !selectedItemIds.includes(item.itemId) ||
                    item.itemId === row.itemId,
                )
                .map((item) => ({
                  value: item.itemId,
                  label: `${item.itemName}`,
                  itemCost: item.unitPrice,
                  unitId: item?.itemUnitResponse?.unitId ?? item.unitId ?? null,
                  unitTitle:
                    item?.itemUnitResponse?.unitTitle ?? item.unitTitle ?? "",
                  conversionMultiplier: item.conversionMultiplier || 1,
                  itemUnitResponse: item.itemUnitResponse || [],
                }));
              const unitOptions = ((row.itemUnitResponse || []) as any)
                ?.filter((u: any) =>
                  row.conversionMultiplier
                    ? u.conversionMultiplier <= row.conversionMultiplier
                    : true,
                )
                .map((u: any) => ({
                  value: u.unitId,
                  label: u.unitTitle,
                }));
              return (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  sx={{ cursor: "pointer" }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={"left"}
                      padding={column.disablePadding ? "none" : "normal"}
                      component={
                        column.id === "itemId" || column.id === "unitId"
                          ? "th"
                          : undefined
                      }
                      id={
                        column.id === "itemId" || column.id === "unitId"
                          ? labelId
                          : undefined
                      }
                      scope={
                        column.id === "itemId" || column.id === "unitId"
                          ? "row"
                          : undefined
                      }
                      sx={{
                        width:
                          column.id === "itemId" || column.id === "unitId"
                            ? "220px"
                            : "auto",
                      }}
                    >
                      {column.id === "itemId" ? (
                        <Controller
                          name={`invoiceItem.${index}.itemId`}
                          control={form.control}
                          render={({ field }) => (
                            <DynamicSelect
                              {...field}
                              label=""
                              value={{ label: row.itemName, value: row.itemId }}
                              options={itemOptions}
                              onChange={(selected) => {
                                field.onChange(selected.value);
                                handleInputChange(
                                  index,
                                  "itemId",
                                  selected.value,
                                );
                                handleInputChange(
                                  index,
                                  "itemName",
                                  selected.label,
                                );
                                handleInputChange(
                                  index,
                                  "unitId",
                                  selected.unitId,
                                );
                                handleInputChange(
                                  index,
                                  "unitTitle",
                                  selected.unitTitle,
                                );
                                handleInputChange(
                                  index,
                                  "unitPrice",
                                  selected.itemCost,
                                );
                                handleInputChange(index, "quantity", 1);
                                handleInputChange(
                                  index,
                                  "itemUnitResponse",
                                  (selected?.itemUnitResponse || [])?.filter(
                                    (u: any) =>
                                      selected.conversionMultiplier
                                        ? u.conversionMultiplier <=
                                          selected.conversionMultiplier
                                        : true,
                                  ),
                                );
                                restoreDeletedItem(selected.value);
                              }}
                            />
                          )}
                        />
                      ) : column.id === "unitId" ? (
                        <Controller
                          name={`invoiceItem.${index}.unitId`}
                          control={form.control}
                          render={({ field }) => (
                            <DynamicSelect
                              {...field}
                              label=""
                              value={{
                                value: row.unitId,
                                label:
                                  row?.unitTitle ||
                                  (row?.itemUnitResponse as any)?.find(
                                    (option: any) =>
                                      option.unitId === row?.unitId,
                                  )?.unitTitle ||
                                  row?.unitId ||
                                  "",
                              }}
                              options={unitOptions}
                              onChange={(selected) => {
                                field.onChange(selected.value);
                                handleInputChange(
                                  index,
                                  "unitId",
                                  selected.value,
                                );
                                handleInputChange(
                                  index,
                                  "unitTitle",
                                  selected.label,
                                );
                                const unit = (row.itemUnitResponse || []).find(
                                  (u: any) => u.unitId === selected.value,
                                );
                                const multiplier =
                                  unit?.conversionMultiplier || 1;

                                // Use base unit price and apply multiplier if available
                                const originalItem = deletedItems
                                  .concat(rows)
                                  .find((r) => r.itemId === row.itemId);
                                const basePrice =
                                  originalItem?.baseUnitPrice ||
                                  originalItem?.unitPrice ||
                                  0;

                                const updatedPrice = basePrice * multiplier;

                                handleInputChange(
                                  index,
                                  "unitPrice",
                                  parseFloat(updatedPrice.toFixed(2)),
                                );
                              }}
                            />
                          )}
                        />
                      ) : column.id === "stockIn" ? (
                        row?.itemType === "PACKAGE_SERVICE_ITEM" ||
                        row?.itemType === "ITEM" ||
                        row?.itemType === "PACKAGE_ITEM" ||
                        row?.itemType === "SERVICE_ITEM" ? (
                          <Controller
                            name={`invoiceItem.${index}.stockIn`}
                            control={form.control}
                            defaultValue={false}
                            render={({ field }) => (
                              <Box
                                sx={{
                                  "& svg": { width: "30px", height: "30px" },
                                  "& .MuiCheckbox-root": { paddingTop: 0 },
                                }}
                              >
                                <CommonCheckbox
                                  {...field}
                                  label={""}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    field.onChange(event.target.checked);
                                    handleInputChange(
                                      index,
                                      "stockIn",
                                      event.target.checked,
                                    );
                                  }}
                                  checked={field.value}
                                  color={themeColors?.primary}
                                />
                              </Box>
                            )}
                          />
                        ) : null
                      ) : (
                        <Box
                          sx={{
                            marginBottom: "10px",
                            "& .MuiTextField-root": { width: "100%" },
                            "& .MuiInputBase-formControl": {
                              border: "1px solid #c7c6c6 !important",
                              marginTop: "10px",
                            },
                          }}
                        >
                          <Controller
                            name={`invoiceItem.${index}.${column.id}`}
                            control={form.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="number"
                                value={field.value || 0}
                                onChange={(e) => {
                                  const val = Math.max(
                                    0,
                                    Number(e.target.value),
                                  );
                                  field.onChange(val);
                                  handleInputChange(index, column.id, val);
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}
                    </TableCell>
                  ))}
                  <TableCell
                    onClick={() => {
                      const actualIndex = page * rowsPerPage + index;
                      setConfirmRowIndex(actualIndex);
                      setConfirm(true);
                    }}
                    className="text-end hide-on-print"
                  >
                    <DeleteIcon />
                  </TableCell>
                  {confirm && (
                    <FormDialog
                      open={confirm}
                      onClose={(event, reason) =>
                        event && reason !== "backdropClick"
                          ? setConfirm(false)
                          : ""
                      }
                      title={t("delete")}
                      content={
                        <Grid container>
                          <Grid item xs={12}>
                            <Typography>
                              {t("Are you sure you want to delete?")}
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                      saveFunction={() => {
                        if (confirmRowIndex !== null) {
                          DeleteItem(rows[confirmRowIndex], confirmRowIndex);
                        }
                        setConfirm(false);
                      }}
                      submitButtonText="Delete"
                    />
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 15, 20, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className="hide-on-print"
        labelRowsPerPage={t("rowsPerPage")}
      />
    </Box>
  );
};
